import {
  carimboDaMensagem,
  TAMANHO_MAXIMO_ANEXO,
  TIPOS_ANEXO_ACEITOS,
  type EstadoChat,
  type MensagemChat,
} from '@mymaxx/shared';
import {
  Anexo,
  arquivoDeAudio,
  Aviso,
  BarraGravacao,
  Botao,
  BotaoMicrofone,
  Citacao,
  useRolagemDoFim,
  useGravadorAudio,
} from '@mymaxx/ui';
import { ArrowLeft, Check, Paperclip, Send, Star } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { ErroApi } from '../lib/api';
import { chatApi, guardarTokenChat } from '../lib/chat-api';
import { ativarAvisoDeMensagem } from '../lib/push';
import { useAvisoNaAba, useTempoRealChat } from '../lib/tempo-real';

/** Versão do aviso de privacidade aceita ao abrir o chat (fica registrada na conversa). */
const VERSAO_PRIVACIDADE = '1.0';

const ROTULO_STATUS: Record<string, string> = {
  bot: 'Atendimento automático',
  fila: 'Na fila',
  em_atendimento: 'Em atendimento',
  aguardando_cliente: 'Aguardando você',
  encerrada: 'Encerrado',
};

/** Tela do cliente: a conversa em si. Abre sem login; o histórico exige conta. */
export function Atendimento() {
  // Setor escolhido na tela da conta (só o técnico escolhe; ver `portas` em Conta.tsx).
  const [parametros] = useSearchParams();
  const setorEscolhido = parametros.get('setor');
  const [estado, setEstado] = useState<EstadoChat | null>(null);
  const [mensagens, setMensagens] = useState<MensagemChat[]>([]);
  const [texto, setTexto] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [digitando, setDigitando] = useState<string | null>(null);
  const [naoLidas, setNaoLidas] = useState(0);
  const fim = useRef<HTMLDivElement>(null);
  const arquivoInput = useRef<HTMLInputElement>(null);
  const [respondendo, setRespondendo] = useState<MensagemChat | null>(null);

  const gravador = useGravadorAudio({
    onErro: setErro,
    onGravado: (audio, mimeType) => void enviarArquivo(arquivoDeAudio(audio, mimeType)),
  });

  useAvisoNaAba(naoLidas);

  // Abre o atendimento (ou retoma o que estava aberto nesta máquina).
  useEffect(() => {
    let cancelado = false;
    void chatApi
      .abrirOuRetomar({
        aceitePrivacidadeVersao: VERSAO_PRIVACIDADE,
        origem: 'web',
        // O técnico escolhe o setor na tela anterior; o servidor confere se aquele setor
        // atende técnico e ignora o pedido se não atender.
        setorId: setorEscolhido ?? undefined,
      })
      .then((sessao) => {
        if (cancelado) return;
        setEstado(sessao);
        setMensagens(sessao.mensagens);
        // Só depois de o atendimento existir: pedir permissão de notificação na abertura
        // da página é o caminho mais curto para a pessoa negar, e negada uma vez o
        // navegador não pergunta de novo.
        void ativarAvisoDeMensagem();
      })
      .catch((e: unknown) => {
        if (!cancelado) setErro(e instanceof ErroApi ? e.mensagem : 'Não consegui abrir o chat');
      });
    return () => {
      cancelado = true;
    };
  }, [setorEscolhido]);

  useRolagemDoFim(fim, mensagens.length);

  const aoReceberMensagem = useCallback((nova: MensagemChat) => {
    // Mesma mensagem chegando de novo substitui a que está na lista, em vez de ser
    // descartada: é assim que o áudio convertido para m4a troca de arquivo embaixo da
    // bolha, sem duplicar nada nem precisar de F5.
    setMensagens((atuais) =>
      atuais.some((m) => m.id === nova.id)
        ? atuais.map((m) => (m.id === nova.id ? nova : m))
        : [...atuais, nova],
    );
    setDigitando(null);
    if (nova.de !== 'cliente' && document.hidden) setNaoLidas((n) => n + 1);
  }, []);

  const aoDigitar = useCallback((nome?: string) => {
    setDigitando(nome ?? 'Atendente');
    setTimeout(() => setDigitando(null), 4000);
  }, []);

  useTempoRealChat(estado?.conversaId ?? null, {
    aoReceberMensagem,
    aoMudarEstado: setEstado,
    aoDigitar,
  });

  // Ao voltar para a aba, zera o contador e recarrega o que chegou enquanto estava fora.
  useEffect(() => {
    const aoVoltar = () => {
      if (document.hidden) return;
      setNaoLidas(0);
      void chatApi
        .mensagens()
        .then(setMensagens)
        .catch(() => undefined);
    };
    document.addEventListener('visibilitychange', aoVoltar);
    return () => document.removeEventListener('visibilitychange', aoVoltar);
  }, []);

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    // Gravando, o mesmo botão de enviar fecha o áudio; o envio segue pelo caminho do
    // anexo assim que o gravador devolver o arquivo.
    if (gravador.gravando) {
      gravador.finalizar();
      return;
    }
    const conteudo = texto.trim();
    if (!conteudo || enviando) return;
    setTexto('');
    setEnviando(true);
    setErro(null);
    try {
      setEstado(await chatApi.enviar(conteudo, respondendo?.id));
      setRespondendo(null);
      setMensagens(await chatApi.mensagens());
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Não consegui enviar');
      setTexto(conteudo);
    } finally {
      setEnviando(false);
    }
  }

  async function enviarArquivo(arquivo: File) {
    if (arquivo.size > TAMANHO_MAXIMO_ANEXO) {
      setErro('Arquivo muito grande (máximo 20 MB).');
      return;
    }
    if (!TIPOS_ANEXO_ACEITOS.includes(arquivo.type)) {
      setErro('Envie imagem, vídeo, áudio, PDF ou documento.');
      return;
    }
    setEnviando(true);
    setErro(null);
    try {
      setEstado(await chatApi.enviarArquivo(arquivo));
      setMensagens(await chatApi.mensagens());
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Não consegui enviar o arquivo');
    } finally {
      setEnviando(false);
      if (arquivoInput.current) arquivoInput.current.value = '';
    }
  }

  if (!estado) {
    return (
      <main className="flex min-h-dvh items-center justify-center px-6">
        {erro ? <Aviso tipo="erro">{erro}</Aviso> : <p className="text-sm">Abrindo o chat…</p>}
      </main>
    );
  }

  const encerrada = estado.status === 'encerrada';

  return (
    <main className="flex min-h-dvh flex-col bg-neutral-50 dark:bg-neutral-950">
      <header className="flex items-center gap-3 border-b border-neutral-200 bg-white px-4 py-3 dark:border-neutral-800 dark:bg-neutral-900">
        <Link to="/" aria-label="Voltar" className="text-neutral-500 hover:text-neutral-800">
          <ArrowLeft size={20} />
        </Link>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">
            {estado.atendente ?? estado.setor?.nome ?? 'MyMAXX'}
          </p>
          <p className="truncate text-xs text-neutral-500">
            {ROTULO_STATUS[estado.status]}
            {estado.posicaoFila ? ` · ${estado.posicaoFila}º da fila` : ''}
            {digitando ? ` · ${digitando} está digitando…` : ''}
          </p>
        </div>
        <span className="shrink-0 rounded bg-neutral-100 px-2 py-1 text-[11px] text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
          {estado.protocolo}
        </span>
      </header>

      <div className="flex-1 space-y-2 overflow-y-auto px-3 py-4">
        {mensagens.map((m) => (
          <Bolha key={m.id} mensagem={m} onResponder={() => setRespondendo(m)} />
        ))}
        <div ref={fim} />
      </div>

      {erro && (
        <div className="px-3 pb-2">
          <Aviso tipo="erro">{erro}</Aviso>
        </div>
      )}

      {encerrada ? (
        <Encerramento estado={estado} onAvaliado={setEstado} />
      ) : (
        <form
          onSubmit={enviar}
          className="flex flex-col gap-2 border-t border-neutral-200 bg-white p-3 dark:border-neutral-800 dark:bg-neutral-900"
        >
          {respondendo && (
            <Citacao
              autor={
                respondendo.de === 'cliente' ? 'Você' : (respondendo.autorNome ?? 'Atendimento')
              }
              resumo={respondendo.texto?.trim() || 'Anexo'}
              onCancelar={() => setRespondendo(null)}
              className="bg-black/5 dark:bg-white/10"
            />
          )}
          <div className="flex items-end gap-2">
            <input
              ref={arquivoInput}
              type="file"
              className="hidden"
              accept={TIPOS_ANEXO_ACEITOS.join(',')}
              onChange={(e) => {
                const arquivo = e.target.files?.[0];
                if (arquivo) void enviarArquivo(arquivo);
              }}
            />
            {/* Gravando, a onda toma o lugar do campo de escrever — lixeira, relógio e
                pausa ali dentro, e quem envia continua sendo o mesmo botão de sempre. */}
            {gravador.gravando ? (
              <BarraGravacao gravador={gravador} />
            ) : (
              <>
                <button
                  type="button"
                  aria-label="Anexar arquivo"
                  onClick={() => arquivoInput.current?.click()}
                  className="rounded-lg p-2 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                >
                  <Paperclip size={20} />
                </button>
                <BotaoMicrofone gravador={gravador} desabilitado={enviando} tamanho={20} />
                <textarea
                  value={texto}
                  rows={1}
                  placeholder="Escreva sua mensagem…"
                  aria-label="Mensagem"
                  onChange={(e) => setTexto(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void enviar(e);
                    }
                  }}
                  className="max-h-32 min-h-10 flex-1 resize-none rounded-lg border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-950"
                />
              </>
            )}
            <Botao
              type="submit"
              carregando={enviando}
              aria-label={gravador.gravando ? 'Enviar áudio' : 'Enviar'}
            >
              <Send size={18} />
            </Botao>
          </div>
        </form>
      )}
    </main>
  );
}

function Bolha({ mensagem, onResponder }: { mensagem: MensagemChat; onResponder: () => void }) {
  if (mensagem.de === 'sistema') {
    return (
      <p className="mx-auto max-w-[90%] rounded bg-neutral-200 px-2 py-1 text-center text-[11px] text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
        {mensagem.texto}
      </p>
    );
  }

  const doCliente = mensagem.de === 'cliente';
  return (
    <div
      className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-line shadow-sm ${
        doCliente
          ? 'ml-auto rounded-br-sm bg-marca-600 text-white'
          : 'mr-auto rounded-bl-sm bg-white dark:bg-neutral-800'
      }`}
    >
      {mensagem.autorNome && !doCliente && (
        <p className="mb-0.5 text-[11px] font-medium opacity-70">{mensagem.autorNome}</p>
      )}
      {mensagem.respondeA && (
        <Citacao
          autor={
            mensagem.respondeA.de === 'cliente'
              ? 'Você'
              : (mensagem.respondeA.autorNome ?? 'Atendimento')
          }
          resumo={mensagem.respondeA.resumo}
        />
      )}
      {mensagem.midia && (
        <Anexo
          url={mensagem.midia.url}
          nomeOriginal={mensagem.midia.nomeOriginal}
          mimeType={mensagem.midia.mimeType}
          tamanho={mensagem.midia.tamanho}
          previaUrl={mensagem.midia.previaUrl}
          // Cliente e técnico estão quase sempre no celular: baixar sozinho joga o
          // arquivo numa pasta que ninguém procura depois.
          perguntarOndeSalvar
        />
      )}
      {mensagem.texto}
      <span className="ml-2 inline-flex items-center gap-1 align-bottom text-[10px] opacity-60">
        {carimboDaMensagem(mensagem.criadaEm)}
        {doCliente && mensagem.lidaEm && <Check size={12} />}
        {/* Sempre visível, não só no passar do mouse: no celular não existe passar o
            mouse, e é lá que o cliente está. */}
        <button type="button" onClick={onResponder} className="underline">
          Responder
        </button>
      </span>
    </div>
  );
}

/** Depois do encerramento: avaliação de 1 a 5 e o protocolo para guardar. */
function Encerramento({
  estado,
  onAvaliado,
}: {
  estado: EstadoChat;
  onAvaliado: (e: EstadoChat) => void;
}) {
  const [nota, setNota] = useState(0);
  const [comentario, setComentario] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // Quem decide é o servidor: encerramento por inatividade, abandono na triagem ou motivo
  // marcado como dispensado não pedem nota. Pedir a quem já saiu não mede nada.
  if (estado.avaliacao || !estado.pedirAvaliacao) {
    return (
      <div className="space-y-2 border-t border-neutral-200 bg-white p-4 text-center dark:border-neutral-800 dark:bg-neutral-900">
        <p className="text-sm font-medium">
          {estado.avaliacao ? 'Obrigado pela avaliação!' : 'Atendimento encerrado.'}
        </p>
        <p className="text-xs text-neutral-500">
          Guarde o protocolo <strong>{estado.protocolo}</strong>.
        </p>
        {/* Solta o token aqui também: este atendimento acabou e não tem mais nada a
            resolver nele. Depender só da conferência na abertura funcionaria, mas deixar
            para trás um token que aponta para conversa encerrada é convite a voltar. */}
        <Link to="/" onClick={() => guardarTokenChat(null)}>
          <Botao variante="secundario" className="w-full">
            Voltar ao início
          </Botao>
        </Link>
      </div>
    );
  }

  async function avaliar() {
    if (!nota) return;
    setEnviando(true);
    setErro(null);
    try {
      onAvaliado(await chatApi.avaliar({ nota, comentario }));
      guardarTokenChat(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Não consegui registrar a avaliação');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="space-y-3 border-t border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900">
      <div className="text-center">
        <p className="text-sm font-medium">Como foi o atendimento?</p>
        <p className="text-xs text-neutral-500">Protocolo {estado.protocolo}</p>
      </div>
      <div className="flex justify-center gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            aria-label={`Nota ${n}`}
            onClick={() => setNota(n)}
            className={n <= nota ? 'text-amber-500' : 'text-neutral-300 dark:text-neutral-600'}
          >
            <Star size={28} fill={n <= nota ? 'currentColor' : 'none'} />
          </button>
        ))}
      </div>
      <textarea
        rows={2}
        value={comentario}
        placeholder="Quer contar mais alguma coisa? (opcional)"
        aria-label="Comentário"
        onChange={(e) => setComentario(e.target.value)}
        className="w-full resize-none rounded-lg border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-950"
      />
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      <Botao
        className="w-full"
        disabled={!nota}
        carregando={enviando}
        onClick={() => void avaliar()}
      >
        Enviar avaliação
      </Botao>
      {/* Sem escapatória: a nota é o que o supervisor usa para saber como foi o
          atendimento, e "Agora não" era o botão que todo mundo clicava. Quem fechar a aba
          sem avaliar volta para esta tela ao tentar abrir outro atendimento — e a
          pendência caduca em 7 dias, para ninguém ficar sem canal por teimosia. */}
      <p className="text-center text-xs text-neutral-500">Sua nota libera o próximo atendimento.</p>
    </div>
  );
}
